import { useFormContext } from "react-hook-form";
import { useGetCodebooksQuery } from "../../../common/queries";
import {
  AsyncSelectInput,
  DateInput,
  SelectInput,
  useAuth,
} from "../../../components";
import LabeledFormControl from "../../../components/forms/LabeledFormControl";
import { SelectItem } from "../../../components/select/Select";
import { oneLineAddress } from "../../../helpers/addressesHelpers";
import { useCustomTranslation } from "../../../helpers/translateHelpers";
import { useValidators } from "../../../helpers/validation";
import {
  CreateRequestFormValues,
  RequestCreateSubFormType,
} from "./RequestCreateFormWrapper";
import { loadServiceObjectsForSelect } from "../../../helpers/serviceObjectHelper";
import { defaultSelectQuery } from "../../../helpers/tableHelpers";
import { useGetCustomerDataQuery } from "../queries";
import { useCustomers } from "../../../helpers/customersHelper";

interface RequestOwnerOrderFieldsProps {
  setVisibleSubForm: (type: RequestCreateSubFormType) => void;
  customAddressItem?: SelectItem;
  customContactPerson?: SelectItem;
  hideServiceObjectInput?: boolean;
  hideCustomerInput?: boolean;
}

const RequestOwnerOrderFields = ({
  setVisibleSubForm,
  customAddressItem,
  customContactPerson,
  hideServiceObjectInput,
  hideCustomerInput,
}: RequestOwnerOrderFieldsProps) => {
  const { tRequest, tInput } = useCustomTranslation();

  const { user } = useAuth();
  const { loadCustomersForFilter } = useCustomers();
  const { watch, setValue } = useFormContext<CreateRequestFormValues>();

  const validators = useValidators();
  const codebooksQuery = useGetCodebooksQuery();

  const customer = watch("customer");

  const customerDataQuery = useGetCustomerDataQuery(customer?.value);

  return (
    <>
      <LabeledFormControl label={tRequest("requested_date")}>
        <DateInput
          name="requestedDate"
          placeholder={tRequest("requested_date_placeholder")}
        />
      </LabeledFormControl>
      <LabeledFormControl isRequired label={tRequest("category")}>
        <SelectInput
          name="category"
          placeholder={tRequest("category_placeholder")}
          items={
            codebooksQuery.data?.orderRequestCategories.map((category) => ({
              label: category.name,
              value: category.id,
            })) ?? []
          }
          required
          options={{
            required: validators.required,
          }}
        />
      </LabeledFormControl>
      {!hideCustomerInput && (
        <LabeledFormControl
          isRequired
          label={tRequest("service_object_operator")}
        >
          <AsyncSelectInput
            name="customer"
            placeholder={tRequest("service_object_operator_placeholder")}
            required
            options={{
              required: validators.required,
              onChange: (e: { target: { value: SelectItem } }) => {
                const { value } = e.target;

                if (value.value !== customer?.value) {
                  setValue("address", null);
                  setValue("contactPerson", null);
                  setValue("serviceObjects", null);
                }
              },
            }}
            loadOptions={(phrase, callback) =>
              loadCustomersForFilter(phrase, callback, "isDeviceUser")
            }
          />
        </LabeledFormControl>
      )}
      <LabeledFormControl isRequired label={tRequest("address")}>
        <SelectInput
          name="address"
          placeholder={tRequest("address_placeholder")}
          items={
            customAddressItem
              ? [
                  customAddressItem,
                  ...(customerDataQuery.data?.addresses.map((address) => ({
                    label: oneLineAddress(address),
                    value: address.id,
                  })) ?? []),
                ]
              : (customerDataQuery.data?.addresses.map((address) => ({
                  label: oneLineAddress(address),
                  value: address.id,
                })) ?? [])
          }
          required
          options={{ required: validators.required }}
          newOption={{
            label: tInput("select_new_address"),
            onClick: () => setVisibleSubForm("AddressSubForm"),
          }}
          isDisabled={!customer}
        />
      </LabeledFormControl>
      <LabeledFormControl isRequired label={tRequest("contact_person")}>
        <SelectInput
          name="contactPerson"
          placeholder={tRequest("contact_person_placeholder")}
          items={
            customContactPerson
              ? [
                  customContactPerson,
                  ...(customerDataQuery.data?.contactPersons.map((person) => ({
                    label: person.fullName,
                    value: person.id,
                  })) ?? []),
                ]
              : (customerDataQuery.data?.contactPersons.map((person) => ({
                  label: person.fullName,
                  value: person.id,
                })) ?? [])
          }
          required
          options={{ required: validators.required }}
          newOption={{
            label: tInput("select_new_contact_person"),
            onClick: () => setVisibleSubForm("ContactPersonSubForm"),
          }}
          isDisabled={!customer}
        />
      </LabeledFormControl>
      {!hideServiceObjectInput && (
        <LabeledFormControl isRequired label={tRequest("service_object")}>
          <AsyncSelectInput
            key={`serviceObjects-${customer?.value ?? "none"}`}
            name="serviceObjects"
            required
            placeholder={tRequest("service_object_placeholder")}
            loadOptions={(phrase, callback) =>
              loadServiceObjectsForSelect(
                {
                  ...defaultSelectQuery,
                  customerId: customer?.value,
                  owningCustomerId: user?.customerId,
                  searchPhrase: phrase,
                },
                callback,
              )
            }
            isMulti
            options={{ required: validators.required }}
            multivalueTagLabelKey="selected_service_objects"
            // Currently we do not support to create a service object inside request
            // newOption={{
            //   label: tInput("select_new_service_object"),
            //   onClick: () =>
            //     props.setVisibleSubForm("ServiceObjectSubForm"),
            // }}
            isDisabled={!customer}
          />
        </LabeledFormControl>
      )}
    </>
  );
};

export default RequestOwnerOrderFields;
